/**
 * The review page's record of the preview the requester saw (`request.preview_summary`, backend
 * `apply_tools.summarize`): how far the change reaches, the notifications it reaches, issues and derived translation
 * bindings -- and, for a franchise licensee's copy of its parent's notifications (P7 `copy_notification_to_own`), what
 * the copy changes for the licensee (`franchise`: the console's FranchiseCopySummary; apiv2 console.openapi.yaml
 * `AIProposalFranchiseSummary`). The person applying the change sees the whole effect.
 *
 * A notification's settings change (notification-settings plan §4.3, P3; backend `change_preview.settings`) adds
 * `settings`, one entry per settings item: shown as the "Settings" section (`SettingsSummary`) in the words of the
 * notification editor -- what changes, whether it sends, who starts and stops receiving it, the other notifications and
 * the franchise's it silences or frees, and its warnings.
 *
 * A content or vocabulary change (client-MCP plan §4.6 stage 5; backend P3-16) describes its records itself
 * (`domain`, `sections`): they render through `ReviewSections`, the same as on the approvals inbox card, under how far
 * the change reaches and what guests see (`guest_exposure`: counts only in a stored summary; a vocabulary change says
 * nothing guests see changes). A preview that ran out of its time budget (`partial`) says what it did not count.
 *
 * The summary is free-form in the contract (`additionalProperties: true`): every value renders as React text, never as
 * HTML; a value of an unexpected type renders as its JSON text; unknown keys are not shown -- an older or newer backend
 * never breaks the page.
 */
import { Alert, AlertTitle, Box, Paper, Typography } from '@mui/material';
import * as React from 'react';

// parts.js imports isCountsOnly from this module; the cycle is harmless: EntryDiff is only read at render time.
import { EntryDiff } from './parts';
import { ReviewSections, sectionsOf } from './ReviewSections';

const text = (v) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
        return JSON.stringify(v);
    } catch (_e) {
        return String(v);
    }
};
const rows = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : []);
const count = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const isObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** The summary as a viewer who does not read the franchise parent's notifications gets it (P7 per viewer, backend
 *  `ai_edit_review._counts_only`): the `franchise` part is counts and a note, its lists empty. Such a viewer cannot
 *  apply the copy (`can_approve` false, `parent_level_required`). */
export const isCountsOnly = (summary) => isObject(summary) && isObject(summary.franchise) && summary.franchise.redacted === true;

/** "4 notifications it reaches belong to the franchise and are not shown." -- `notifications_not_shown` (the parent's,
 *  and the copies a preview made, which carry its names) for such a viewer. Null when there are none. */
export function franchiseNotShown(n, what = 'shown') {
    const k = count(n);
    if (!k) return null;
    return k === 1
        ? `1 notification it reaches belongs to the franchise and is not ${what}.`
        : `${k} notifications it reaches belong to the franchise and are not ${what}.`;
}

const COUNTS = [
    ['stops_receiving', "The franchise's notifications that stop"],
    ['copies', 'copies'],
    ['not_copied', 'integrations that stop'],
    ['fixed_recipients', 'fixed recipients kept'],
    ['kept_parent_references', 'franchise rows kept'],
];

// "The franchise's notifications that stop: 3 · copies: 3 (2 created disabled) · …" -- a counts-only part without its
// note.
function countsLine(counts) {
    const c = isObject(counts) ? counts : {};
    return COUNTS.map(([key, label]) => {
        const n = count(c[key]) ?? 0;
        const disabled = key === 'copies' ? count(c.disabled_copies) : null;
        return `${label}: ${n}${disabled ? ` (${disabled} created disabled)` : ''}`;
    }).join(' · ');
}

// React keys from each entry's ids (two entries may read alike); an id set that repeats -- the same entry sent twice --
// gets a "#n" suffix, so a key is never shared.
function keyed(entries, keyOf, textOf) {
    const seen = new Map();
    return entries.map((entry) => {
        const base = keyOf(entry);
        const n = seen.get(base) || 0;
        seen.set(base, n + 1);
        return { key: n ? `${base}#${n}` : base, text: textOf(entry) };
    });
}

// "<channel> (delivery method #<id>) ": a delivery method's entry names it, like the not-copied ones.
const method = (channel, dm) => (channel ? `${text(channel)}${dm !== undefined && dm !== null ? ` (delivery method #${text(dm)})` : ''} ` : '');

// `more`: a line after the entries (a count of entries not shown); it stands in for the empty marker.
const Lines = ({ label, lines, more = null }) => (
    <Box data-lines sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
        {lines.length === 0 && !more ? (
            <Typography variant="body2">—</Typography>
        ) : (
            lines.map((line) => (
                <Typography key={line.key} variant="body2" sx={{ wordBreak: 'break-word' }}>{line.text}</Typography>
            ))
        )}
        {more && <Typography variant="body2" color="text.secondary">{more}</Typography>}
    </Box>
);

// "3 kept references to franchise rows are not shown." -- `kept_parent_references_not_shown` (P7 round 7): the
// parent's tags and tag categories a copy keeps, counted for a viewer without franchise-level tag access. Null when
// absent, zero or not a count.
function keptNotShown(n) {
    const k = count(n);
    if (!Number.isInteger(k) || k <= 0) return null;
    return k === 1 ? '1 kept reference to a franchise row is not shown.' : `${k} kept references to franchise rows are not shown.`;
}

// "Reaches 1 notification · 12 upcoming bookings · 4 departures · 2 tours"; a capped count reads "N+".
function reachLine(summary) {
    const radius = isObject(summary.blast_radius) ? summary.blast_radius : null;
    const notifications = count(radius?.notifications) ?? count(summary.notifications)
        ?? (Array.isArray(summary.notifications) ? summary.notifications.length : null);
    const more = radius?.capped === true ? '+' : '';
    const parts = [];
    if (notifications !== null) parts.push(plural(notifications, 'notification', 'notifications'));
    [['bookings', 'upcoming booking', 'upcoming bookings'], ['departures', 'departure', 'departures'], ['tours', 'tour', 'tours']]
        .forEach(([key, one, many]) => {
            const n = count(radius?.[key]);
            if (n !== null) parts.push(`${n}${more} ${n === 1 && !more ? one : many}`);
        });
    return parts.length ? `Reaches ${parts.join(' · ')}` : null;
}

// "What guests see: 2 upcoming trips show it in the app · 1 notification reads it" (`guest_exposure`, a content
// change: counts only in a stored summary); a vocabulary change's `note`. Null when there is neither.
function guestExposureLine(exposure) {
    if (!isObject(exposure)) return null;
    if (typeof exposure.note === 'string' && exposure.note) return exposure.note;
    const trips = rows(exposure.trips).length + (count(exposure.trips_not_shown) ?? 0);
    const notifications = rows(exposure.notifications).length + (count(exposure.notifications_not_shown) ?? 0);
    return `What guests see: ${plural(trips, 'upcoming trip shows', 'upcoming trips show')} it in the app · `
        + `${plural(notifications, 'notification reads', 'notifications read')} it`;
}

const PARTIAL = 'The preview ran out of its time budget: how far the change reaches and what guests see were not counted. '
    + 'The changes per record are complete.';

// ---- a notification's settings change (`settings`) ----------------------------------------------------------------

const own = (map, key) => (typeof key === 'string' && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null);

// The editor's names for the settings (backend `change_preview.settings.SCALARS`, `TARGET_SETS`, the rules row).
const SETTING_LABELS = {
    enabled: 'Enabled',
    event: 'Event',
    booking_state: 'Booking state',
    tour_override_mode: 'Tour override mode',
    apply_to_guests: 'Sends to guests',
    apply_to_observers: 'Sends to observers',
    rules_match_type: 'Rule matching',
    tours: 'Tours',
    tags: 'Tags',
    roles: 'Roles',
    atag: 'Access tags',
    rules: 'Rules',
};
// The words the backend sends for stored integers (`settings_tools.vocabulary`), as the editor shows them.
const SETTING_VALUES = {
    booking_state: { on_hold: 'On hold', booked: 'Booked', cancelled: 'Cancelled', cancelled_on_hold: 'Cancelled on hold', pending: 'Pending confirmation' },
    tour_override_mode: { send_always: 'Non-overridable', overridable: 'Overridable', tour_specific: 'Tour specific' },
    rules_match_type: { all: 'Match all', any: 'Match any' },
};
const CHANNEL_NAMES = {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    chat: 'Chat',
    airbnb: 'Airbnb',
    push: 'Push notification',
    http: 'HTTP request',
    sns: 'AWS SNS',
    sentry: 'Sentry',
    other: 'Other',
};
const SENDING = {
    starts: 'It starts sending.',
    stops: 'It stops sending.',
    'keeps sending': 'It keeps sending.',
    'stays silent': 'It stays off: nothing is sent before or after the change.',
};

// "quiet_hours" -> "Quiet hours": a setting a newer backend adds still reads as words.
const humanize = (key) => {
    const s = text(key).replace(/_/g, ' ').trim();
    return s ? s[0].toUpperCase() + s.slice(1) : 'Setting';
};
const settingValue = (field, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return own(own(SETTING_VALUES, field) || {}, v) || text(v);
};
const channelName = (channel) => own(CHANNEL_NAMES, channel) || humanize(channel);

// Counts of the side that hit the preview's row cap read "N+" (`reach.capped`).
const capMark = (n, cap) => `${n}${cap && cap.capped && n === cap.max ? '+' : ''}`;
const upcoming = (n, cap) => `${capMark(n, cap)} upcoming booking${n === 1 ? '' : 's'}`;

function sendingText(sending) {
    if (!isObject(sending)) return null;
    const known = own(SENDING, sending.change);
    if (known) return known;
    if (typeof sending.before !== 'boolean' || typeof sending.after !== 'boolean') return null;
    if (sending.before === sending.after) return SENDING[sending.after ? 'keeps sending' : 'stays silent'];
    return SENDING[sending.after ? 'starts' : 'stops'];
}

// "Upcoming bookings it reaches: 12 → 9 · 1 starts receiving it · 4 stop receiving it"
function reachText(reach, cap) {
    if (!isObject(reach)) return null;
    const before = count(reach.before);
    const after = count(reach.after);
    if (before === null || after === null) return null;
    const parts = [`Upcoming bookings it reaches: ${capMark(before, cap)} → ${capMark(after, cap)}`];
    const start = count(reach.start);
    const stop = count(reach.stop);
    if (start !== null) parts.push(`${start} ${start === 1 ? 'starts' : 'start'} receiving it`);
    if (stop !== null) parts.push(`${stop} ${stop === 1 ? 'stops' : 'stop'} receiving it`);
    return parts.join(' · ');
}

// What a channel's fixed recipients are (`settings_tools._recipients`: only the field its destination uses).
function recipientsText(recipients) {
    if (!isObject(recipients)) return null;
    if (Array.isArray(recipients.addresses)) return recipients.addresses.length ? recipients.addresses.map(text).join(', ') : 'no address';
    if ('number' in recipients) return recipients.number ? text(recipients.number) : 'no number';
    if ('support_contact' in recipients) {
        const contact = recipients.support_contact;
        if (isObject(contact)) return text(contact.name) || `#${text(contact.id)}`;
        return recipients.hidden_reference === true ? 'a contact that is not shown' : 'none set';
    }
    return null;
}

// One side of a channel row: "On · to Arbitrary address: ops@demo.test · 9 upcoming bookings"; "—" where it did not
// exist. An integration channel (HTTP, SNS, Sentry) is listed without any of its fields.
function channelSide(side, bookings, cap) {
    if (!isObject(side)) return '—';
    const parts = [];
    if (side.enabled === true || side.enabled === false) parts.push(side.enabled ? 'On' : 'Off');
    const destination = side.integration === true ? '' : text(side.destination_label) || (side.destination ? humanize(side.destination) : '');
    if (destination) {
        const recipients = recipientsText(side.recipients);
        parts.push(`to ${destination}${recipients ? `: ${recipients}` : ''}`);
    }
    const n = count(bookings);
    if (n !== null) parts.push(upcoming(n, cap));
    return parts.join(' · ') || '—';
}

function rulesSides(row) {
    const side = (n) => (count(n) === null ? '—' : plural(n, 'rule', 'rules'));
    const changes = [['added', 'added'], ['removed', 'removed'], ['changed', 'changed']]
        .filter(([key]) => count(row[key]) > 0)
        .map(([key, word]) => `${row[key]} ${word}`);
    return [side(row.before), `${side(row.after)}${changes.length ? ` (${changes.join(', ')})` : ''}`];
}

/**
 * A settings entry's `diff` as `EntryDiff` reads it (`{snapshot: 'ok', fields, m2m}`), in words: the scalars, the rules
 * row and each channel as before → after rows, the targeting sets as added / removed names; plus the notes under it
 * (rows of other companies counted, a channel's note). Every row label is unique (EntryDiff keys rows by it).
 */
function settingsDiff(entry, cap) {
    const seen = new Map();
    const unique = (label) => {
        const n = (seen.get(label) || 0) + 1;
        seen.set(label, n);
        return n > 1 ? `${label} (${n})` : label;
    };
    const move = isObject(entry.event_move) ? entry.event_move : {};
    const timings = new Map([move.from, move.to].filter(isObject).map((s) => [s.code, typeof s.timing === 'string' ? s.timing : '']));
    const eventText = (code) => (timings.get(code) ? `${text(code)} (${timings.get(code)})` : settingValue('event', code));
    const fields = [];
    const m2m = [];
    const notes = [];
    rows(entry.diff).forEach((row) => {
        if (row.field === 'channels') {
            const name = channelName(row.channel);
            const label = unique(row.id === null || row.id === undefined ? `New ${name} channel` : `${name} channel #${text(row.id)}`);
            const bookings = isObject(row.bookings) ? row.bookings : {};
            fields.push({ field: label, before: channelSide(row.before, bookings.before, cap), after: channelSide(row.after, bookings.after, cap) });
            if (row.note) notes.push(`${label} — ${text(row.note)}`);
        } else if (row.field === 'rules') {
            const [before, after] = rulesSides(row);
            fields.push({ field: unique(SETTING_LABELS.rules), before, after });
        } else if (Array.isArray(row.added) || Array.isArray(row.removed)) {
            const label = unique(own(SETTING_LABELS, row.field) || humanize(row.field));
            const names = (list) => rows(list).map((r) => text(r.name) || `#${text(r.id)}`);
            m2m.push({ field: label, added: names(row.added), removed: names(row.removed) });
            const hidden = count(row.not_shown);
            if (hidden > 0) notes.push(`${label}: ${hidden} more ${hidden === 1 ? 'is' : 'are'} not shown.`);
        } else {
            const label = unique(own(SETTING_LABELS, row.field) || humanize(row.field));
            const value = (v) => (row.field === 'event' ? eventText(v) : settingValue(row.field, v));
            fields.push({ field: label, before: value(row.before), after: value(row.after) });
        }
    });
    return { diff: { snapshot: 'ok', fields, m2m }, notes };
}

// "custom" / "standard" / "standard and custom": whether its channels use standard or custom templates (`structured`).
const templatesWord = (s) => {
    if (!isObject(s)) return null;
    if (s.mixed === true) return 'standard and custom';
    if (s.structured === true) return 'standard';
    return s.structured === false ? 'custom' : null;
};

// "#4 Everyone · <event> — now replaced by a tour-specific notification on 1 tour (Canal Walk): 6 upcoming bookings
// stop receiving it" (`siblings`: the tour-specific suppression the change moves).
function siblingText(sibling) {
    const tours = (list) => rows(list).map((t) => text(t.name) || `#${text(t.id)}`);
    const bookings = isObject(sibling.bookings) ? sibling.bookings : {};
    const tail = (n, one, many) => {
        const k = count(n);
        return k === null ? '' : `: ${upcoming(k)} ${k === 1 ? one : many}`;
    };
    const parts = [];
    const silenced = tours(sibling.silenced_tours);
    if (silenced.length) {
        parts.push(`now replaced by a tour-specific notification on ${plural(silenced.length, 'tour', 'tours')} (${silenced.join(', ')})`
            + `${tail(bookings.silenced, 'stops receiving it', 'stop receiving it')}`);
    }
    const freed = tours(sibling.unsilenced_tours);
    if (freed.length) {
        parts.push(`no longer replaced on ${plural(freed.length, 'tour', 'tours')} (${freed.join(', ')})`
            + `${tail(bookings.unsilenced, 'receives it again', 'receive it again')}`);
    }
    const head = `#${text(sibling.id)} ${text(sibling.name)} · ${text(sibling.event)}`;
    return parts.length ? `${head} — ${parts.join('; ')}` : head;
}

// `franchise`: on a licensee, the franchise's notifications its change silences and lets send again (counted for a
// viewer without franchise-level notification access, backend `counts_only`); on a franchise parent, the licensees
// and their bookings its notification reaches.
function SettingsFranchise({ franchise }) {
    if (!isObject(franchise)) return null;
    if (franchise.role === 'franchise_parent') {
        const reached = isObject(franchise.reached) ? franchise.reached : {};
        const bookings = isObject(franchise.licensee_bookings) ? franchise.licensee_bookings : {};
        const cap = { capped: bookings.capped === true, max: Math.max(count(bookings.before) ?? 0, count(bookings.after) ?? 0) };
        const side = (v) => (count(v) === null ? '—' : capMark(v, cap));
        const of = count(franchise.licensees) !== null ? ` of ${franchise.licensees}` : '';
        return (
            <Typography variant="body2" sx={{ mt: 1 }}>
                {`Licensees it reaches: ${count(reached.before) ?? '—'} → ${count(reached.after) ?? '—'}${of} · `
                    + `their upcoming bookings: ${side(bookings.before)} → ${side(bookings.after)}`}
            </Typography>
        );
    }
    if (franchise.redacted === true) {
        const note = typeof franchise.note === 'string' && franchise.note ? franchise.note : null;
        const counts = isObject(franchise.counts) ? franchise.counts : {};
        return note ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-word' }}>{note}</Typography>
        ) : (
            <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                    {`The franchise's notifications it silences: ${count(counts.silenced) ?? 0} · that send again: ${count(counts.unsilenced) ?? 0}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    The details are shown to franchise staff with franchise-level notification access.
                </Typography>
            </Box>
        );
    }
    const line = (n) => `#${text(n.id)} ${text(n.name)} · ${text(n.event)}${count(n.bookings) !== null ? ` · ${upcoming(n.bookings)}` : ''}`;
    const silenced = keyed(rows(franchise.silenced), (n) => text(n.id), line);
    const freed = keyed(rows(franchise.unsilenced), (n) => text(n.id), line);
    return (
        <>
            {silenced.length > 0 && <Lines label="The franchise's notifications it silences" lines={silenced} />}
            {freed.length > 0 && <Lines label="The franchise's notifications that send again" lines={freed} />}
        </>
    );
}

// The event move (`event_move`: the old and the new timing; what may be received twice or not at all) and the
// item's warnings, in one box.
function SettingsWarnings({ entry, testId }) {
    const move = isObject(entry.event_move) ? entry.event_move : null;
    const warnings = Array.isArray(entry.warnings) ? entry.warnings.filter((w) => w !== null && w !== undefined && w !== '') : [];
    const side = (s) => (isObject(s) && s.code ? `${text(s.code)}${s.timing ? ` (${text(s.timing)})` : ''}` : null);
    const from = move && side(move.from);
    const to = move && side(move.to);
    const twice = move ? count(move.may_receive_twice) : null;
    const nothing = move ? count(move.get_nothing) : null;
    if (!(from && to) && warnings.length === 0) return null;
    return (
        <Alert severity="warning" data-testid={testId} sx={{ mt: 1 }}>
            {from && to && <AlertTitle sx={{ wordBreak: 'break-word' }}>{`The event changes from ${from} to ${to}.`}</AlertTitle>}
            {from && to && twice !== null && nothing !== null && (
                <Typography variant="body2">
                    {`Right now: ${plural(twice, 'booking', 'bookings')} may receive it twice, ${plural(nothing, 'booking', 'bookings')} would get nothing.`}
                </Typography>
            )}
            {warnings.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {/* keyed by position: a static list, and two warnings may read alike */}
                    {warnings.map((w, i) => (
                        <Typography key={i} component="li" variant="body2" sx={{ wordBreak: 'break-word' }}>{text(w)}</Typography>
                    ))}
                </Box>
            )}
        </Alert>
    );
}

function SettingsItem({ entry }) {
    const nf = isObject(entry.notification) ? entry.notification : {};
    const label = text(entry.label) || text(nf.name) || (nf.id !== undefined && nf.id !== null ? `Notification #${text(nf.id)}` : 'Notification');
    const reach = isObject(entry.reach) ? entry.reach : null;
    const cap = reach ? { capped: reach.capped === true, max: Math.max(count(reach.before) ?? 0, count(reach.after) ?? 0) } : null;
    const sending = sendingText(entry.sending);
    const reachLineText = reachText(reach, cap);
    const { diff, notes } = settingsDiff(entry, cap);
    const structured = isObject(entry.structured) ? entry.structured : {};
    const templates = [templatesWord(structured.before), templatesWord(structured.after)];
    const siblings = keyed(rows(entry.siblings), (s) => `${text(s.id)}:${text(s.event)}`, siblingText);
    return (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }} data-testid={`settings-item-${text(entry.item)}`}>
            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>{label}</Typography>
            {sending && <Typography variant="body2">{sending}</Typography>}
            {reachLineText && <Typography variant="body2">{reachLineText}</Typography>}
            {reachLineText && typeof reach.basis === 'string' && reach.basis && (
                <Typography variant="caption" color="text.secondary" component="div">{`Counted from ${reach.basis}.`}</Typography>
            )}
            <Box sx={{ mt: 1 }}>
                {diff.fields.length || diff.m2m.length ? (
                    <EntryDiff diff={diff} label={`Settings changes of ${label}`} />
                ) : (
                    <Typography variant="body2" color="text.secondary">No setting changes were recorded.</Typography>
                )}
                {notes.map((note) => (
                    <Typography key={note} variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>{note}</Typography>
                ))}
            </Box>
            {templates[0] && templates[1] && templates[0] !== templates[1] && (
                <Typography variant="body2" sx={{ mt: 1 }}>{`Templates: ${templates[0]} → ${templates[1]}`}</Typography>
            )}
            {siblings.length > 0 && <Box sx={{ mt: 1 }}><Lines label="Other notifications it changes" lines={siblings} /></Box>}
            <SettingsFranchise franchise={entry.franchise} />
            <SettingsWarnings entry={entry} testId={`settings-warnings-${text(entry.item)}`} />
        </Paper>
    );
}

/** The "Settings" section: one card per settings item (`summary.settings`). Null when there is none. */
export function SettingsSummary({ settings }) {
    const entries = keyed(rows(settings), (e) => text(e.item), (e) => e);
    if (entries.length === 0) return null;
    return (
        <Box data-testid="settings-summary" sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">Settings</Typography>
            {entries.map(({ key, text: entry }) => <SettingsItem key={key} entry={entry} />)}
        </Box>
    );
}

export function PreviewSummary({ summary }) {
    if (!isObject(summary)) return null;
    const reach = reachLine(summary);
    const basis = isObject(summary.blast_radius) && typeof summary.blast_radius.basis === 'string' ? summary.blast_radius.basis : null;
    const notifications = rows(summary.notifications);
    const issues = count(summary.issues);
    const derived = isObject(summary.derived) ? summary.derived : null;
    const derivedCounts = derived ? ['kept', 'added', 'removed'].map((k) => count(derived[k]) ?? 0) : null;
    const notShown = franchiseNotShown(summary.notifications_not_shown);
    const settings = rows(summary.settings);
    const guests = guestExposureLine(summary.guest_exposure);
    const guestBasis = guests && isObject(summary.guest_exposure) && typeof summary.guest_exposure.basis === 'string'
        ? summary.guest_exposure.basis : null;
    const partial = summary.partial === true;
    const sections = sectionsOf(summary).length > 0;
    const anything = reach || notifications.length || issues || notShown || settings.length || guests || partial || sections
        || (derivedCounts && derivedCounts.some((n) => n > 0));
    return (
        <Box data-testid="preview-summary" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">What the preview showed</Typography>
            {!anything && <Typography variant="body2" color="text.secondary">No details were recorded.</Typography>}
            {reach && <Typography variant="body2">{reach}</Typography>}
            {reach && basis && (
                <Typography variant="caption" color="text.secondary" component="div">{`Counted from ${basis}.`}</Typography>
            )}
            {guests && <Typography variant="body2">{guests}</Typography>}
            {guestBasis && guestBasis !== basis && (
                <Typography variant="caption" color="text.secondary" component="div">{`Counted from ${guestBasis}.`}</Typography>
            )}
            {partial && <Typography variant="body2" color="text.secondary">{PARTIAL}</Typography>}
            {issues > 0 && <Typography variant="body2">{`The preview found ${plural(issues, 'issue', 'issues')}.`}</Typography>}
            {derivedCounts && derivedCounts.some((n) => n > 0) && (
                <Typography variant="body2">
                    {`Translation bindings: ${derivedCounts[0]} kept · ${derivedCounts[1]} added · ${derivedCounts[2]} removed`}
                </Typography>
            )}
            {notifications.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" component="div">Notifications it reaches</Typography>
                    {keyed(notifications, (n) => text(n.id), (n) => n).map(({ key, text: n }) => (
                        <Box key={key} sx={{ mb: 0.5 }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{`#${text(n.id)} ${text(n.name)} · ${text(n.event)}`}</Typography>
                            {n.reasons !== undefined && n.reasons !== null && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ wordBreak: 'break-word' }}>
                                    {Array.isArray(n.reasons) ? n.reasons.map(text).join('; ') : text(n.reasons)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
            {notShown && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{notShown}</Typography>}
            <SettingsSummary settings={settings} />
            <ReviewSections summary={summary} />
        </Box>
    );
}

export function FranchiseCopySummary({ franchise }) {
    if (!isObject(franchise)) return null;
    if (franchise.redacted === true) {
        // Counts only: the backend's note says them; no list section (they are empty by design).
        const note = typeof franchise.note === 'string' && franchise.note ? franchise.note : null;
        return (
            <Box data-testid="franchise-copy" sx={{ mt: 2 }}>
                <Typography variant="subtitle1">Copy to own (franchise)</Typography>
                {note ? (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{note}</Typography>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{countsLine(franchise.counts)}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                            The details are shown to franchise staff with franchise-level notification access.
                        </Typography>
                    </>
                )}
            </Box>
        );
    }
    const notCopied = rows(franchise.not_copied);
    const fixed = rows(franchise.fixed_recipients);
    const kept = rows(franchise.kept_parent_references);
    const keptHidden = keptNotShown(franchise.kept_parent_references_not_shown);
    return (
        <Box data-testid="franchise-copy" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Copy to own (franchise)</Typography>
            {franchise.note !== undefined && franchise.note !== null && franchise.note !== '' && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, wordBreak: 'break-word' }}>{text(franchise.note)}</Typography>
            )}
            <Lines
                label="Stops receiving (the parent's)"
                lines={keyed(rows(franchise.stops_receiving), (n) => text(n.id), (n) => `#${text(n.id)} ${text(n.name)} · ${text(n.event)}`)}
            />
            <Lines
                label="Replaced by (the licensee's copies)"
                lines={keyed(rows(franchise.replaced_by), (c) => text(c.copy_of), (c) => `${text(c.name)} · copy of #${text(c.copy_of)}${
                    c.enabled === false
                        ? (c.why_disabled ? ` — created disabled: ${text(c.why_disabled)}` : " — disabled, as the parent's is")
                        : ''
                }`)}
            />
            {notCopied.length > 0 && (
                <Lines
                    label="Not copied: stop running for the licensee"
                    lines={keyed(notCopied, (m) => `${text(m.notification)}:${text(m.delivery_method)}`, (m) => `#${text(m.notification)} ${text(m.name)} · `
                        + `${text(m.channel)} (delivery method #${text(m.delivery_method)}): `
                        + `${text(m.reason)} — ${Array.isArray(m.fields) ? m.fields.map(text).join(', ') : text(m.fields)}`)}
                />
            )}
            {fixed.length > 0 && (
                <Lines
                    label="Fixed recipients (keep receiving)"
                    lines={keyed(fixed, (r) => `${text(r.notification)}:${text(r.delivery_method)}:${text(r.field)}`,
                        (r) => `#${text(r.notification)} · ${method(r.channel, r.delivery_method)}${text(r.field)}: ${text(r.value)}`)}
                />
            )}
            {(kept.length > 0 || keptHidden) && (
                <Lines
                    label="Parent rows the copies keep using"
                    more={keptHidden}
                    lines={keyed(kept, (k) => `${text(k.notification)}:${text(k.delivery_method)}:${text(k.field)}:${text(k.row)}`,
                        (k) => `#${text(k.notification)} ${text(k.name)} · ${method(k.channel, k.delivery_method)}${text(k.field)}: `
                            + `${text(k.label)} (#${text(k.row)})`)}
                />
            )}
        </Box>
    );
}
